import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '@/stores/authStore'
import { apiClient } from '@/lib/api'
import { useQueryClient } from '@tanstack/react-query'

function navLinkClass({ isActive }: { isActive: boolean }) {
  return [
    'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
    isActive
      ? 'bg-neutral-900 text-white'
      : 'text-neutral-700 hover:bg-neutral-100',
  ].join(' ')
}

export function AppShell() {
  const { user, workspace, clear } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const isSuperadmin = user?.role === 'SUPERADMIN'
  const isOwner = user?.role === 'OWNER' || user?.role === 'SUPERADMIN'

  async function logout() {
    try {
      await apiClient.post('/api/v1/auth/logout')
    } finally {
      clear()
      qc.removeQueries({ queryKey: ['session'] })
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <header className="border-b bg-white">
        <div className="mx-auto max-w-6xl px-4 h-14 flex items-center gap-6">
          <Link to="/" className="font-semibold text-neutral-900">Iznir</Link>
          <nav className="flex items-center gap-1 flex-1">
            <NavLink to="/dashboard" className={navLinkClass}>Dashboard</NavLink>
            <NavLink to="/skills" className={navLinkClass}>Skills</NavLink>
            <NavLink to="/marketplace" className={navLinkClass}>Marketplace</NavLink>
            {isOwner && <NavLink to="/settings/workspace" className={navLinkClass}>Settings</NavLink>}
            {isSuperadmin && <NavLink to="/admin" className={navLinkClass}>Admin</NavLink>}
          </nav>
          <div className="text-sm text-neutral-600 flex items-center gap-3">
            {user && workspace && (
              <span className="hidden sm:inline">
                {user.name} · <span className="text-neutral-500">{workspace.name}</span>
                {' · '}
                <span className="text-xs uppercase tracking-wide text-neutral-500">{user.role}</span>
              </span>
            )}
            <button
              onClick={logout}
              className="text-sm text-neutral-600 hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}
